import React, { useRef, useState } from "react";
import {
    FormField,
    Button,
    BusinessPortalHeader,
    LottieLoadingIndicator,
    Toast,
    ImageCropModal,
    PortalFormActions,
    PortalFormDescription,
    PortalFormScrollBody,
    PortalFormFieldGroup,
    PortalFormLabel,
    PortalFormSection,
    PortalFormSectionDescription,
    PortalFormSectionHeader,
    PortalFormSectionTitle,
    PortalFormSurface,
    SegmentedButtonGroup,
    StandardModalActionButton,
    StandardModalActionGroup,
    portalColors
} from "src/components";
import { Grid } from "@mui/material";
import styled from "styled-components";
import { USER_GROUP, API_ERROR_FIELD } from "src/constants/donations";
import Dropzone from "react-dropzone";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";

const InputLabel = PortalFormLabel;
const InputDescription = PortalFormDescription;

const TextInput = styled(FormField)`
    font-family: Manrope, Helvetica, sans-serif;
    height: 46px;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    text-align: left;
    font-weight: 400;
    font-size: 0.938rem;
    color: ${portalColors.text};
    margin: 0;

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
        font-family: Manrope, Helvetica, sans-serif;
        font-size: 0.938rem;
    }
`;

const TextArea = styled.textarea`
    font-family: Manrope, Helvetica, sans-serif;
    color: ${portalColors.text};
    font-size: 0.938rem;
    font-weight: 400;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    padding: 10px 20px;
    resize: none;
    height: 8.5rem;
    width: 100%;
    box-sizing: border-box;

    ::placeholder {
        text-align: left;
        color: ${portalColors.textSoft};
        font-family: Manrope, Helvetica, sans-serif;
        font-size: 0.938rem;
    }

    &:focus {
        border: 1px solid ${portalColors.text};
        box-shadow: 0 0 0 4px rgba(91, 110, 245, 0.12);
        outline: none;
    }
`;

const ErrorMessage = styled.div`
    font-size: 11.5px;
    color: #ff7171;
    font-weight: 500;
    line-height: 12.5px;
    margin: 4px 0 0 4px;

    &:empty {
        display: none;
    }
`;

const StyledDropzone = styled(Dropzone)`
    height: 13.25rem;
    width: min(100%, 380px);
    border-radius: 16px;
    border: 1px solid ${portalColors.border};
    background: ${portalColors.surfaceSoft};
    margin: 0.313rem 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    overflow: hidden; /* Ensure image doesn't overflow the container */
`;

const ImageContainer = styled.div`
    width: 100%;
    height: 13.25rem;
    margin: ${p => p.margin || "0"};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
`;

const UploadInfo = styled.div`
    padding: 0 1rem;
    box-sizing: border-box;
`;

const UploadButtonRow = styled.div`
    margin-top: 1rem;
    text-align: center;
`;

const ImagePreview = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain; /* Image will fit inside container without stretching */
`;

const FileInput = styled.input`
    display: none;
`;

const FormPage = ({
    handleFormVisible,
    formData,
    updatingUuid,
    isFormSubmitting,
    formErrors,
    handleInputChange,
    handleChangeUserGroup,
    handleFormSubmit,
    setSelectedImage,
    selectedImage
}) => {

    // Crop Image
    const [imageToCrop, setImageToCrop] = useState(null);
    const [showCropperModal, setShowCropperModal] = useState(false);
        
    const handleCropComplete = (file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (selectedImage) {
            URL.revokeObjectURL(selectedImage);
        }
        setShowCropperModal(false);
        setSelectedImage(prev => {
            return { 
                ...prev, 
                readerResult: url,
                file,
                hasBeenUploaded: false,
                s3Url: null
            };
        });
    };

    const userGroupOptions = [
        { value: USER_GROUP.EMPLOYEES, label: `All ${capitalizeString(pluralize(customNames.employees))}` },
        { value: USER_GROUP.CLIENTS, label: `All ${capitalizeString(pluralize(customNames.clients))}` },
        { value: USER_GROUP.BOTH, label: "Both" }
    ];
    const getErrorMessage = input => {
        const error = formErrors.find(error => error.input === input);
        return error ? `* ${error.message}` : null;
    };

    const dropzoneRef = useRef();
    const handleBrowseClick = () => {
        if (dropzoneRef.current) {
            dropzoneRef.current.open();
        }
    };

    const handleDrop = acceptedFiles => {
        const validImageTypes = ["image/jpeg", "image/png"];
       
        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (validImageTypes.includes(file.type)) {
                    setSelectedImage(prev => {
                        return { 
                            ...prev, 
                            readerResult: reader.result,
                            hasBeenUploaded: false,
                            s3Url: null,
                            file: null,
                        };
                    });
                    setImageToCrop(reader.result);
                    setShowCropperModal(true);

                } else {
                    Toast.error("Unsupported file type. Images may be JPEG or PNG format.");
                    return;
                }
            };
        }
    };

    return (
        <>
            <BusinessPortalHeader
                title={updatingUuid ? "Edit Donation Organisation" : "Create New Donation Organisation"}
                description="Add a donation opportunity by uploading an image, providing details about the cause, and linking to the donation page. Engage users in supporting important causes through your business."
                maxWidth="84.688rem"
            />

            <PortalFormSurface maxWidth="84.688rem" marginTop="0" flex="1 1 auto" minHeight="0">
                <PortalFormScrollBody columns="minmax(0, 1.2fr) minmax(20rem, 0.8fr)" padding="1.25rem" gap="1rem">
                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>
                                Donation Information
                            </PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Define the audience, organisation details, headline, description and destination link.
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>

                        <Grid container direction="column" spacing={2}>

                            <Grid item>
                                <PortalFormFieldGroup>
                                    <InputLabel>
                                        Select Donation User Group
                                    </InputLabel>
                                    <InputDescription margin="0.25rem 0 0.5rem">
                                        Select to display this donation to employees, clients or both user groups.
                                    </InputDescription>
                                </PortalFormFieldGroup>
                                <SegmentedButtonGroup
                                    options={userGroupOptions}
                                    value={formData.userGroupType}
                                    minWidth="9.6rem"
                                    disabled={isFormSubmitting}
                                    onChange={handleChangeUserGroup}
                                />
                                <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.USER_GROUP_TYPE) ?? "" }</ErrorMessage>
                            </Grid>
                            <Grid item>
                                <InputLabel>
                                    Name Of Organisation
                                </InputLabel>
                                <InputDescription>
                                    Enter the name of the donation organisation.
                                </InputDescription>
                                <TextInput
                                    name="organisationName"
                                    value={formData.organisationName ?? ""}
                                    placeholder="e.g., “Red Cross“"
                                    onChange={handleInputChange}
                                    disabled={isFormSubmitting}
                                />
                                <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.ORGANISATION_NAME) ?? "" }</ErrorMessage>
                            </Grid>

                            <Grid item>
                                <InputLabel>
                                    Headline
                                </InputLabel>
                                <InputDescription>
                                    Enter a clear and engaging headline for your donation.
                                </InputDescription>
                                <TextInput
                                    name="headline"
                                    value={formData.headline ?? ""}
                                    placeholder="e.g., “Support the Red Cross: Help Those in Need”"
                                    onChange={handleInputChange}
                                    disabled={isFormSubmitting}
                                />
                                <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.HEADLINE) ?? "" }</ErrorMessage>
                            </Grid>

                            <Grid item>
                                <InputLabel>
                                    Donation Description
                                </InputLabel>
                                <InputDescription>
                                    Provide a brief description of the donation, outlining the benefits and details for users.
                                </InputDescription>
                                <TextArea
                                    name="description"
                                    value={formData.description ?? ""}
                                    placeholder="e.g., “Join us in supporting the Red Cross, an organisation dedicated to providing emergency assistance, disaster relief, and education. Your contribution can make a difference in the lives of those affected by crises.”"
                                    onChange={handleInputChange}
                                    disabled={isFormSubmitting}
                                />
                                <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.DESCRIPTION) ?? "" }</ErrorMessage>
                            </Grid>

                            <Grid item>
                                <InputLabel>
                                    Donation Website Page
                                </InputLabel>
                                <InputDescription>
                                    Enter the website URL for the organisation&apos;s donation page.
                                </InputDescription>
                                <TextInput
                                    name="donationPageUrl"
                                    value={formData.donationPageUrl ?? ""}
                                    placeholder="e.g., www.companyname.com"
                                    onChange={handleInputChange}
                                    disabled={isFormSubmitting}
                                />
                                <ErrorMessage>{ getErrorMessage(API_ERROR_FIELD.PAGE_URL) ?? "" }</ErrorMessage>
                            </Grid>
                        </Grid>
                    </PortalFormSection>

                    <PortalFormSection>
                        <PortalFormSectionHeader withDivider>
                            <PortalFormSectionTitle>
                                Upload Donation Image
                            </PortalFormSectionTitle>
                            <PortalFormSectionDescription>
                                Add a widescreen image or logo that represents the organisation.
                            </PortalFormSectionDescription>
                        </PortalFormSectionHeader>

                        <InputLabel>
                            Upload Image
                        </InputLabel>
                        <InputDescription>
                            Upload the organisation logo or an image representing the donation.
                        </InputDescription>
                        
                        <StyledDropzone ref={dropzoneRef} onDrop={handleDrop}>
                            <ImageContainer>
                                {
                                    selectedImage.readerResult || formData.imageUrl ? (
                                        <ImagePreview src={selectedImage.readerResult || formData.imageUrl} alt="Donation Image" />
                                    ) : (
                                        <>
                                            <UploadButtonInfo handleBrowseClick={() => {/** leave this blank as Dropzone already have event */}}/>
                                        </>
                                    )
                                }
                       
                                <FileInput type="file" accept="image/*" onChange={handleDrop} />
                            </ImageContainer>
                        </StyledDropzone>    
                        { (selectedImage.readerResult || formData.imageUrl) && <UploadButtonInfo handleBrowseClick={handleBrowseClick} /> }
                        <ErrorMessage style={{ textAlign: "center" }}>{ getErrorMessage(API_ERROR_FIELD.IMAGE) ?? "" }</ErrorMessage>
                    </PortalFormSection>
                </PortalFormScrollBody>

                <PortalFormActions sticky={false}>
                    <StandardModalActionGroup $pushRight>
                        <StandardModalActionButton type="button" onClick={handleFormVisible} disabled={isFormSubmitting}>
                            Cancel
                        </StandardModalActionButton>
                        <StandardModalActionButton
                            type="button"
                            $variant="primary"
                            onClick={handleFormSubmit}
                            disabled={isFormSubmitting}
                        >
                            {isFormSubmitting
                                ? <LottieLoadingIndicator height="1.5rem" width="1.5rem" containerHeight="1.5rem" margin="0" />
                                : "Save"}
                        </StandardModalActionButton>
                    </StandardModalActionGroup>
                </PortalFormActions>
                <ImageCropModal
                    imageSrc={imageToCrop}
                    isOpen={showCropperModal}
                    onClose={(shouldClear) => {
                        setShowCropperModal(false);
                        if (shouldClear) {
                            setImageToCrop(null);
                            setSelectedImage(prev => {
                                return { 
                                    ...prev, 
                                    readerResult: null,
                                    hasBeenUploaded: false,
                                    s3Url: null,
                                    file: null,
                                };
                            });
                        }
                    }}
                    onCropComplete={handleCropComplete}
                    aspectRatios={[
                        { width: 16, height: 9, label: "16:9", standardName: ASPECT_RATIO_LABEL.WIDESCREEN },
                    ]}
                    allowedOrientations={["landscape"]}
                    lockedAspectRatio={undefined}
                />
            </PortalFormSurface>
        </>
    );
};

const UploadButtonInfo = ({ handleBrowseClick }) => {
    return (
        <>
            <UploadInfo>
                <InputDescription color="#6A7387" size="0.938rem" align="left">
                    Supported formats: JPG, PNG. Max size: 5MB. <br />
                    Aspect Ratio 16:9 - 1920px x 1080px
                </InputDescription>
            </UploadInfo>
            <UploadButtonRow>
                <Button
                    backgroundColor="var(--calendar-action-primary-bg, #5B6EF5)"
                    color="var(--calendar-action-primary-text, #fff)"
                    border="1px solid var(--calendar-action-primary-bg, #5B6EF5)"
                    size="0.813rem"
                    style={{ borderRadius: "8px", boxShadow: "none" }}
                    onClick={handleBrowseClick}
                >
                    Browse File
                </Button>
            </UploadButtonRow>
        </>
    );
};
export default FormPage;
